import React, { useCallback, useEffect, useState } from 'react';

import { useHotkeys } from 'react-hotkeys-hook';

import { cn } from '../../lib/utils';

import SpinnerIcon from '../../../assets/images/spinner.svg';

import { KEYCHAIN, useKeyMaster } from '../../contexts/KeyMaster';
import { DirectMessageRouteProvider, RouteWorkspaceProvider } from '../../contexts/RouteWorkspaceContext';
import SurfaceErrorBoundary from '../shared/SurfaceErrorBoundary';
import WidgetStudio from './WidgetStudio';

// The takeover expands into place and contracts away rather than flying in off the edge
// of the window. Dismissal runs shorter than presentation — getting out of the way should
// feel quicker than arriving.
const COMPOSER_ENTER_MS = 420;
const COMPOSER_EXIT_MS = 240;

const ComposerLoadingState = React.memo(function ComposerLoadingState() {
  return (
    <div className="flex flex-1 items-center justify-center" role="status" aria-label="Loading widget composer">
      <SpinnerIcon className="size-5 animate-spin text-gray-400" />
    </div>
  );
});

// The panel chrome is already on screen by the time this hydrates, so the scope only
// swaps its own contents rather than the whole surface.
const ComposerWorkspaceScope = React.memo(function ComposerWorkspaceScope({ workspaceId, directMessageId, children }) {
  const loadingFallback = <ComposerLoadingState />;

  if (directMessageId) {
    return (
      <DirectMessageRouteProvider directMessageId={directMessageId} includeEntitySnapshots={false} loadingFallback={loadingFallback}>
        {children}
      </DirectMessageRouteProvider>
    );
  }

  return (
    <RouteWorkspaceProvider workspaceId={workspaceId} includeEntitySnapshots={false} loadingFallback={loadingFallback}>
      {children}
    </RouteWorkspaceProvider>
  );
});

// The drawer's "New" action opens the widget studio as a takeover rather than a centered
// modal: it covers the rail's chat rows and the chat column, stopping at the widgets
// pane's edge so the board stays lit and usable while you build against it. The panel
// runs flush to the region's edges.
//
// `bounds` is the region it takes over, measured against the app shell — the caller owns
// it because the widgets pane's width and the mobile snap panels only exist up there.
//
// It is deliberately not a ModalPortal — that portals to the body and can only be sized
// against the viewport, so it could not carve the board out. Escape and the KeyMaster
// stack are wired up here instead so stacked dialogs (delete confirmation) win the key.
const WidgetComposerOverlay = ({ open, session, workspaceId, directMessageId, channelId, bounds, onClose }) => {
  const { register, unregister, unlocked } = useKeyMaster();
  // Mount ahead of the enter transition and hold through the exit one, so the builder
  // is never torn down mid-slide (and never bootstraps a chat while closed).
  const [isMounted, setIsMounted] = useState(open);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(
    function playComposerTransition() {
      if (open) {
        setIsMounted(true);
        const frameId = window.requestAnimationFrame(() => setHasEntered(true));
        return () => window.cancelAnimationFrame(frameId);
      }

      setHasEntered(false);
      const timerId = window.setTimeout(() => setIsMounted(false), COMPOSER_EXIT_MS);
      return () => window.clearTimeout(timerId);
    },
    [open],
  );

  useEffect(
    function claimKeyboardWhileOpen() {
      if (!open) return undefined;

      register(KEYCHAIN.MODAL__WIDGET_COMPOSER);
      return () => unregister(KEYCHAIN.MODAL__WIDGET_COMPOSER);
    },
    [open, register, unregister],
  );

  const handleEscape = useCallback(
    function closeComposerOnEscape() {
      if (!unlocked(KEYCHAIN.MODAL__WIDGET_COMPOSER)) return;
      onClose();
    },
    [onClose, unlocked],
  );

  useHotkeys('escape', handleEscape, { enabled: open, enableOnFormTags: true, enableOnContentEditable: true, preventDefault: true }, [
    handleEscape,
  ]);

  const handleGutterPointerDown = useCallback(
    function closeComposerOnGutterPress(event) {
      // Only the 12px surround dismisses; presses inside the panel bubble up here too.
      if (event.target !== event.currentTarget) return;
      onClose();
    },
    [onClose],
  );

  if (!isMounted || !workspaceId) return null;

  const targetWidgetId = session?.widgetId || null;
  const targetAgentChatId = session?.agentChatId || null;

  // The app shell is the positioning ancestor, so the takeover can reach across the rail;
  // `bounds` pulls its right edge back to leave the widgets pane showing. Nothing clips
  // here — the panel's shadow has to spill past the gutter and fall across the board,
  // since it is the only thing separating the two surfaces now.
  return (
    <>
      {/* Modal-weight dim across the whole shell: the widgets pane stays visible but
          becomes backdrop while the studio is open. Same tone as the ModalPortal
          backdrop; a press on it dismisses, like any modal's outside press. */}
      <div
        onPointerDown={handleGutterPointerDown}
        style={{ transitionDuration: `${hasEntered ? COMPOSER_ENTER_MS : COMPOSER_EXIT_MS}ms` }}
        className={cn(
          'absolute inset-0 z-(--stack-widget-composer) bg-black/60 transition-opacity ease-out motion-reduce:transition-none',
          hasEntered ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <div
        onPointerDown={handleGutterPointerDown}
        style={bounds}
        className={cn(
          'absolute inset-y-0 z-(--stack-widget-composer) pr-3',
          // Matches the pane's own open/close slide, so closing the board while the
          // composer is up widens it in step instead of snapping.
          'transition-[right] duration-300 ease-fluid motion-reduce:transition-none',
          hasEntered ? '' : 'pointer-events-none',
        )}
      >
        {/* Grows out of the board's edge instead of travelling in from off-window: the right
          edge is pinned by the origin, so it unfolds leftward from the pane that launched
          it and never sweeps across the widgets on the way.

          Leaving is a straight fade at full size. Shrinking on the way out read as the
          panel collapsing into nothing — arriving wants presence, leaving just wants to
          be out of the way. `open` (not hasEntered) separates the two: both the frame
          before entering and the whole exit sit at hasEntered === false. */}
        <div
          role="dialog"
          aria-label="Create widget"
          style={{
            transitionDuration: `${hasEntered ? COMPOSER_ENTER_MS : COMPOSER_EXIT_MS}ms`,
            transitionTimingFunction: `var(${hasEntered ? '--ease-settle' : '--ease-exit'})`,
          }}
          className={cn(
            // gray-200 is the app shell's base tone (and the house Modal surface): the
            // studio blocks like the main app in light mode — a clearly darker shell for
            // the chrome, with the content pane stepping lighter on top of it. The tight
            // dark shadow ring just outside the white hairline is what keeps the stroke
            // legible against the chat behind the panel.
            'flex size-full min-h-0 flex-col overflow-hidden border-[0.5px] border-white bg-gray-200 text-black',
            'shadow-[0_0_0_0.5px_rgba(0,0,0,0.10),0_12px_80px_rgba(0,0,0,0.45)]',
            // No will-change here. Pinning the panel to its own compositor layer hands the
            // rounded-corner clip to the compositor, which rasterizes it without the main
            // thread's anti-aliasing — the corners come back visibly stepped.
            'origin-top-right transition-[opacity,transform] motion-reduce:transition-none',
            hasEntered ? 'opacity-100' : 'opacity-0',
            // No transform once it has landed — not even an identity scale. A transformed
            // box rasterizes its rounded-corner clip in transformed space, which is what
            // steps the corners. The scale exists only for the frame before entering.
            open && !hasEntered ? 'scale-[0.97]' : '',
          )}
        >
          <SurfaceErrorBoundary surface="widget-composer">
            <ComposerWorkspaceScope workspaceId={workspaceId} directMessageId={directMessageId}>
              <WidgetStudio
                variant="composer"
                channelId={channelId}
                initialWidgetId={targetWidgetId}
                // Only a targeted open (a banner, a board tile's Edit) carries a chat in. New
                // resumes nothing: the board's banners are the way back to an unfinished
                // build now, so reopening on the last draft only fought the empty state.
                initialAgentChatId={targetAgentChatId}
                initialView={session?.view || null}
                onClose={onClose}
              />
            </ComposerWorkspaceScope>
          </SurfaceErrorBoundary>
        </div>
      </div>
    </>
  );
};

export default WidgetComposerOverlay;
